/**
 * Search Results
 *
 * A component to handle the search results page
 *
 * Child Components:
 * - Tabs.js
 * - ...
 */

/**
 * React dependencies
 */

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * DWT components
 */
import ArticleTab from './ArticleTab';
import BookTab from './BookTab';
import ContributorTab from './ContributorTab';
import Tabs from './Tabs';

/**
 * DWT hooks
 */

const SearchResults = () => {
  const [ isActive, setIsActive ] = useState( '' );
  const [ articleData, setArticleData ] = useState( [] );
  const [ articleRecordCount, setArticleRecordCount ] = useState( 0 );
  const [ articlePageCount, setArticlePageCount ] = useState( 0 );
  const [ articlePageCurrent, setArticlePageCurrent ] = useState( 0 );
  const [ prhBookData, setPrhBookData ] = useState( [] );
  const [ bookRows, setBookRows ] = useState( 0 );
  const [ bookRecordCount, setBookRecordCount ] = useState( 0 );
  const [ prhContributorData, setPrhContributorData ] = useState( [] );
  const [ contributorRows, setContributorRows ] = useState( 0 );
  const [ contributorRecordCount, setContributorRecordCount ] = useState( 0 );
  const [ loading, setLoading ] = useState( true );
  const [ tabsRequested, setTabsRequested ] = useState( 0 );

  /*
  GET SEARCH TERM
  */
  const [ searchTerm, setSearchTerm ] = useState( '' );
  const getSearchTerm = ( url ) => {
    const urlParams = new URLSearchParams( url );

    if ( !urlParams.has( 'keyword' ) ) {
      return;
    }

    const cleanTerm = urlParams.get( 'keyword' );
    setSearchTerm( cleanTerm );
  };

  const divisionNames = window.DP.search_division_names?.join();
  const imprintNames = window.DP.search_imprint_names?.join();
  const contribRoleCode = window.DP.contributor_roles?.map( ( role ) => role.value )
    .join();
  const isAudio = divisionNames?.includes( 'Audio' ) ?? false;

  const setupRequest = async( endpoint, method, body = null ) => {
    const baseUrl = window.location.origin;
    const ajaxNonce = typeof ajax_object !== 'undefined' ? ajax_object.nonce : null;
    const response = await fetch( `${baseUrl}/${endpoint}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // eslint-disable-next-line camelcase, no-undef
        'X-WP-Header': ajaxNonce
      },
      body
    });

    if (
      endpoint.includes( 'wp' ) &&
            response.headers.get( 'X-WP-Total' ) !== null &&
            response.headers.get( 'X-WP-TotalPages' ) !== null
    ) {
      setArticleRecordCount( parseInt( response.headers.get( 'X-WP-Total' ) ) );
      setArticlePageCount( parseInt( response.headers.get( 'X-WP-TotalPages' ) ) );
    }

    const responseData = await response.json();

    return responseData;
  };

  const makeRequest = ( endpoint, method, body ) => {
    setupRequest( endpoint, method, body )
      .then( ( response ) => {
        if (
          method === 'POST' && ( isAudio
            ? response.params?.docType === 'isbn'
            : response.params?.docType === 'work' )
        ) {
          // Change data in books to match what Book.js is expecting
          response.data.results.map( ( item ) => {
            item.title = item.name;
            return item;
          });
          setPrhBookData( ( prhBookData ) => [
            ...prhBookData,
            ...response.data.results
          ] );
          setBookRecordCount( response.recordCount );
        }
        if (
          method === 'POST' && response.params?.docType === 'author'
        ) {
          response.data.results.map( ( item ) => {
            item.display = item.name;
            return item;
          });
          setPrhContributorData( ( prhContributorData ) => [
            ...prhContributorData,
            ...response.data.results
          ] );
          setContributorRecordCount( response.recordCount );
        }
        if ( method === 'GET' ) {
          setArticleData( ( articleData ) => [
            ...articleData,
            ...response
          ] );
        }
      })
      .catch( ( error ) => {
        console.error( error );
      })
      .finally( () => {
        setTabsRequested( ( tabs ) => tabs + 1 );
        setLoading( false );
      });
  };

  const loadMoreBooks = () => {
    setLoading( true );
    let body = '';
    if ( isAudio ) {
      body = JSON.stringify({
        q: searchTerm,
        docType: 'isbn',
        rows: 24,
        start: bookRows,
        division: divisionNames,
        imprints: imprintNames,
      });
    } else {
      body = JSON.stringify({
        q: searchTerm,
        docType: 'work',
        rows: 24,
        start: bookRows,
        division: divisionNames,
        imprints: imprintNames,
      });
    }
    setBookRows( ( bookRows ) => bookRows + 24 );
    makeRequest( `wp-json/dwt/v2/list-search`, 'POST', body );
  };

  const loadMoreContributors = () => {
    setLoading( true );
    const body = JSON.stringify({
      q: searchTerm,
      docType: 'author',
      rows: 24,
      start: contributorRows,
      division: divisionNames,
      contribRoleCode,
      imprints: imprintNames,
    });
    setContributorRows( ( contributorRows ) => contributorRows + 24 );
    makeRequest( `wp-json/dwt/v2/list-search`, 'POST', body );
  };

  const loadMoreArticles = () => {
    setLoading( true );
    const newArticlePageCurrent = articlePageCurrent + 1;
    setArticlePageCurrent( newArticlePageCurrent );
    makeRequest(
      `wp-json/wp/v2/search/?search=${searchTerm}&per_page=24&page=${newArticlePageCurrent}&_embed`,
      'GET'
    );
  };

  useEffect( () => {
    getSearchTerm( window.location.search );

    if ( searchTerm !== '' ) {
      loadMoreBooks();
      loadMoreContributors();
      loadMoreArticles();
    }
  }, [ searchTerm ] );

  if ( isActive === '' && tabsRequested >= 3 ) {
    if ( bookRecordCount > 0 ) {
      setIsActive( 'books' );
    } else if ( contributorRecordCount > 0 ) {
      setIsActive( 'contributors' );
    } else if ( articleRecordCount > 0 ) {
      setIsActive( 'articles' );
    }
  }

  return (
    <div className="search">
      <div className="search-results-header relative px-[20px] md:px-[50px]">
        <div className="search-results-header-text max-w-lg py-[45px] lg:m-auto">
          {( tabsRequested < 3 ||
                        bookRecordCount > 0 ||
                        contributorRecordCount > 0 ||
                        articleRecordCount > 0 ) && (
            <h2 className="heading-2">
                            Displaying search results for &ldquo;{searchTerm}
                            &rdquo;
            </h2>
          )}
          {tabsRequested >= 3 &&
          bookRecordCount === 0 &&
          contributorRecordCount === 0 &&
          articleRecordCount === 0 && (
            <div>
              <h2 className="heading-2">
                                    Your search for &ldquo;{searchTerm}&rdquo;
                                    returned no results.
              </h2>
              <h3 className="paragraph-1 mt-[25px]">
                                    Check the spelling, try a more general term,
                                    or use fewer words.
              </h3>
            </div>
          )}
        </div>
        <div className="search-results__main py-10">
          <Tabs
            isActive={isActive}
            setIsActive={setIsActive}
            bookRecordCount={bookRecordCount}
            contributorRecordCount={contributorRecordCount}
            articleRecordCount={articleRecordCount}
          />
        </div>
      </div>
      <div className="px-[20px] md:px-[50px]">
        <div className="mx-auto my-32 max-w-lg">
          {isActive === 'books' && bookRecordCount > 0 && (
            <BookTab
              prhData={prhBookData}
              recordCount={bookRecordCount}
              searchTerm={searchTerm}
              loadMore={loadMoreBooks}
              loading={loading}
            />
          )}

          {isActive === 'contributors' &&
                        contributorRecordCount > 0 && (
            <ContributorTab
              prhData={prhContributorData}
              recordCount={contributorRecordCount}
              searchTerm={searchTerm}
              loadMore={loadMoreContributors}
              loading={loading}
            />
          )}

          {isActive === 'articles' && articleRecordCount > 0 && (
            <ArticleTab
              articleData={articleData}
              recordCount={articleRecordCount}
              searchTerm={searchTerm}
              loadMore={loadMoreArticles}
              loading={loading}
              currentPage={articlePageCurrent}
              totalPages={articlePageCount}
            />
          )}

          {tabsRequested >= 3 &&
          bookRecordCount === 0 &&
          contributorRecordCount === 0 &&
          articleRecordCount === 0 && (
            <div className="mt-24 flex justify-center">
              <h2>No results returned.</h2>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const renderSearchResults = () => {
  const searchResults = document.querySelector( '.page-search-results #main' );

  if ( !searchResults ) {
    return;
  }

  const root = createRoot( searchResults );

  root.render( <SearchResults /> );
};

export default renderSearchResults;
